import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { spring } from '../../utils/motion';
interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  hideTitle?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'md' | 'lg';
  tall?: boolean;
}
export function Sheet({
  open,
  onClose,
  title,
  hideTitle,
  children,
  footer,
  size = 'md',
  tall
}: SheetProps) {
  const isDesktop = useIsDesktop();
  const controls = useDragControls();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-825" data-mp="p88zvoaya7sj">
      {open && <div className="fixed inset-0 z-[60]" data-id="element-826" data-mp="tb5pw02tl66i">
          <motion.div className="absolute inset-0 bg-black/45" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} onClick={onClose} data-id="element-827" data-mp="tc8cvwx0uoh5" />
          <motion.div role="dialog" aria-modal="true" aria-label={title} initial={isDesktop ? {
        x: '100%'
      } : {
        y: '100%'
      }} animate={isDesktop ? {
        x: 0
      } : {
        y: 0
      }} exit={isDesktop ? {
        x: '100%'
      } : {
        y: '100%'
      }} transition={spring.sheet} drag={isDesktop ? false : 'y'} dragControls={controls} dragListener={false} dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={{
        top: 0,
        bottom: 0.6
      }} onDragEnd={(_, info) => {
        if (info.offset.y > 110 || info.velocity.y > 600) onClose();
      }} className={twMerge('absolute flex flex-col bg-bg shadow-2xl', isDesktop ? twMerge('right-0 top-0 h-full w-full rounded-l-lg', size === 'lg' ? 'max-w-[560px]' : 'max-w-[440px]') : twMerge('inset-x-0 bottom-0 max-h-[92dvh] rounded-t-lg', tall && 'h-[92dvh]'))} data-id="element-828" data-mp="5ktay595q96f">
            {!isDesktop && <div className="flex cursor-grab touch-none justify-center pb-1 pt-3 active:cursor-grabbing" onPointerDown={e => controls.start(e)} data-id="element-829" data-mp="2aelfuukv1gt">
                <span className="h-1.5 w-10 rounded-full bg-line" data-id="element-830" data-mp="bb2zgpjz11tp" />
              </div>}
            <div className={twMerge('flex items-center justify-between gap-4 px-5 pb-2', isDesktop ? 'pt-5' : 'pt-1')} data-id="element-831" data-mp="1rot3xxf05s3v">
              <h2 className={twMerge('text-xl font-bold tracking-[-0.01em]', hideTitle && 'sr-only')} data-id="element-832" data-mp="1v55o3h3gqwg0">{title}</h2>
              <button type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-833" data-mp="1z6rapu5sup3e">
                <XIcon className="h-5 w-5" data-id="element-834" data-mp="2iui87c0bwox3" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5" data-id="element-835" data-mp="2iwktxjug7vw0">{children}</div>
            {footer && <div className="border-t border-line px-5 py-4" data-id="element-836" data-mp="294ow89w9y7sb">{footer}</div>}
          </motion.div>
        </div>}
    </AnimatePresence>, document.body);
}